import { datadogLogs } from '@datadog/browser-logs';
import axios, { type AxiosError } from 'axios';
import { StatusCodes } from 'http-status-codes';
import { useEffect } from 'react';
import { Cookies } from 'react-cookie';

import { cacheStore } from '@/hooks/useRTM/store';
import { type IRootStore } from '@/mobx/root-store';

import useLoggedUser from '../hooks/useLoggedUser';
import useMixpanel from '../hooks/useMixpanel';

const validNonUsersUrls = [
  'dashboard/api/login',
  'dashboard/api/login_with_google',
  '/api/sso/urls',
  '/api/auth/discovery',
  '/api/logout',
  '/api/login_cognito',
  '/api/login_otp',
  '/api/login_with_otp',
  '/api/clinician/v2/auth/code',
  '/api/camel/auth/register_with_cognito/',
  '/api/camel/auth/register_with_token/',
  '/api/camel/auth/forgot_password_flow',
];
const validNonClinicsUrls = [
  '/api/my_profile', // Deprecate
  '/api/v2/my_profile', // Deprecate
  '/api/camel/config/my_profile',
  '/api/refresh_auth_token',
];
const cookies = new Cookies();
axios.defaults.withCredentials = true;

export default function useAxiosInterceptors(store: IRootStore) {
  const { track } = useMixpanel();
  const { logout } = useLoggedUser();

  function requestClinicInterceptor() {
    return axios.interceptors.request.use((request) => {
      if (!request.cancelToken) {
        request.cancelToken = store.authStore.cancelSource.token;
      }

      if (
        validNonClinicsUrls.includes(request?.url ?? '') ||
        validNonUsersUrls.includes(request?.url ?? '')
      ) {
        return request;
      }

      const clinicUuid = store.appStore.clinicUuid;
      if (!clinicUuid) {
        throw new axios.Cancel(`Canceled by the user, no clinicUuid ${request.url}`);
      }

      return request;
    });
  }

  function requestUserInterceptor() {
    return axios.interceptors.request.use(
      (request) => {
        const token = cookies.get('auth_timeout');
        if (!request.cancelToken) {
          request.cancelToken = store.authStore.cancelSource.token;
        }

        if (
          !token &&
          request?.url?.startsWith('/') &&
          !validNonUsersUrls.includes(request?.url ?? '')
        ) {
          logout({ cancelSource: store.authStore.cancelSource, preserveRedirect: true });
          throw new axios.Cancel(`Canceled by user, no token. url: ${request.url}`);
        }

        return request;
      },
      (error) => {
        return Promise.reject(error);
      },
    );
  }

  function responseErrorInterceptor() {
    return axios.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error instanceof axios.Cancel) {
          console.info('Request canceled by the app');
          console.info(error);
          return Promise.resolve({});
        } else if (error.code == 'ECONNABORTED') {
          datadogLogs?.logger?.error('web_timeout_error', {
            url: error.config?.url,
            timeout: error.config?.timeout,
            params: error.config?.params,
          });
        } else {
          datadogLogs?.logger?.error('API Error:', {
            status: error.response?.status,
            url: error.config?.url,
            method: error.config?.method,
            data: error.response?.data,
          });
          if (error?.response?.status === StatusCodes.UNAVAILABLE_FOR_LEGAL_REASONS) {
            console.error('permission_issue', {
              url: error?.config?.url,
              clinic: store?.appStore?.clinicUuid,
              method: error?.config?.method,
            });
            track('permission_issue', {
              url: error?.config?.url,
              clinic: store?.appStore?.clinicUuid,
              method: error?.config?.method,
            });
          }

          const requestedUrl = error.request.responseURL;
          const isLoginAttemptRefused = validNonUsersUrls.includes(error.config?.url ?? '');
          if (
            error?.response?.status === StatusCodes.UNAUTHORIZED &&
            !isLoginAttemptRefused &&
            (requestedUrl.includes('clinic.onestep') || requestedUrl.includes('localhost'))
          ) {
            logout({ cancelSource: store.authStore.cancelSource, preserveRedirect: true });
            cacheStore.dispose();
          }
        }
        return Promise.reject(error);
      },
    );
  }
  // @TODO: What da f***?!
  useEffect(() => {
    const userInterceptorId = requestUserInterceptor();
    const clinicInterceptorId = requestClinicInterceptor();
    const responseErrorInterceptorId = responseErrorInterceptor();

    return () => {
      axios.interceptors.request.eject(userInterceptorId);
      axios.interceptors.request.eject(clinicInterceptorId);
      axios.interceptors.response.eject(responseErrorInterceptorId);
    };
  }, [store.appStore.clinicUuid, logout]);
}
